import {
  type ApolloClient,
  gql,
  type NormalizedCacheObject,
} from "@apollo/client";
import type { AtomInfos, CONTEXT_KEY, EntityInfos } from "@atoms/atom-types";
import type { Delta } from "jsondiffpatch";
import {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AtomStore } from "./AtomStore";
import { useAuthToken } from "./auth/AuthProvider";
import { useBackendConnection } from "./connection/backendConnectionStore";
import { initApolloClient2 } from "./initApolloClient";
import type { SelectorFunction } from "./use-atom/makeUseAtom";

export const subscriptionDocument = gql`
  subscription subscribeAtom($type: String!, $context: JSON!, $parameters: JSON) {
    subscribeAtom(type: $type, context: $context, parameters: $parameters)
  }
`;

export type SubscriptionCallback<RType extends object = object> = (
  value: RType | null,
  patches: Delta | undefined,
  computedPatches?: boolean,
) => void;

export type SubscriptionProps<RType extends object = object> = {
  current: Promise<ZenObservable.Subscription>;
  /** after the last subscriber is removed, the websocket subscription remains open for a second to see if something is resubscribed */
  unsubscribeTimeout?: ReturnType<typeof setTimeout>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  selectors: Array<SelectorFunction<any, any>>;
  callbacks: Array<SubscriptionCallback<RType>>;
  lastValue: RType | null | undefined;
  completed?: boolean;
  error?: Error;
  atomType?: string;
  context?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
};

export type SubscriptionCallbacks = { [path: string]: SubscriptionProps };

export const AtomStoreContext = createContext<AtomStore | null>(null);

interface AtomStoreProviderProps {
  children: ReactNode;
  atomInfosExtended: AtomInfos<string, string>;
  entityInfos: EntityInfos<string, CONTEXT_KEY<string>>;
}
export const AtomStoreProvider = ({
  children,
  atomInfosExtended,
  entityInfos,
}: AtomStoreProviderProps) => {
  const getAuthToken = useAuthToken();
  const { isConnected } = useBackendConnection();
  const [apolloClient, setApolloClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);

  useEffect(() => {
    const { apolloClient, wsLink } = initApolloClient2(getAuthToken);
    setApolloClient(apolloClient);

    return () => {
      apolloClient?.stop();
      wsLink.client.dispose();
    };
  }, [getAuthToken]);

  // Important: keep hook order stable across renders.
  // On the first render, apolloClient is null; we still must call useMemo.
  const atomStore = useMemo(() => {
    if (!apolloClient) return null;
    return new AtomStore(apolloClient, atomInfosExtended, entityInfos);
  }, [apolloClient, atomInfosExtended, entityInfos]);

  // On reconnect, reopen subscriptions to avoid missing intermediate updates.
  useEffect(() => {
    if (!atomStore) return;
    if (!isConnected) return;
    void atomStore.resubscribeAll();
  }, [atomStore, isConnected]);

  if (!atomStore) {
    return null;
  }

  return (
    <AtomStoreContext.Provider value={atomStore}>
      {children}
    </AtomStoreContext.Provider>
  );
};
